import React, { useEffect, useRef } from 'react';
import { Alert, Box, Link, Typography } from '@mui/material';
import type { ActionConfirmationProps, ActionConfirmationVariant } from '../../types';

const VARIANT_CONFIG: Record<
  ActionConfirmationVariant,
  { role: 'status' | 'alert'; 'aria-live': 'polite' | 'assertive'; severity: 'success' | 'error' | 'info' | 'warning' }
> = {
  success: { role: 'status', 'aria-live': 'polite', severity: 'success' },
  info: { role: 'status', 'aria-live': 'polite', severity: 'info' },
  error: { role: 'alert', 'aria-live': 'assertive', severity: 'error' },
  warning: { role: 'alert', 'aria-live': 'assertive', severity: 'warning' },
};

/**
 * Standardized inline confirmation for key actions. Renders near the source of the action.
 * Copy guidelines: success = "Action completed."; error = "What went wrong."; keep concise.
 * Accessible: ARIA live region, role status/alert, keyboard-dismissible close.
 */
const ActionConfirmation: React.FC<ActionConfirmationProps> = ({
  visible,
  variant,
  message,
  onDismiss,
  id,
  autoHideDuration = 6000,
  dismissible = true,
  linkLabel,
  linkOnClick,
}) => {
  const config = VARIANT_CONFIG[variant];
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!visible || autoHideDuration <= 0) return;
    timeoutRef.current = setTimeout(onDismiss, autoHideDuration);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [visible, autoHideDuration, onDismiss]);

  if (!visible || !message) return null;

  return (
    <Box
      id={id}
      component="div"
      role={config.role}
      aria-live={config['aria-live']}
      aria-atomic="true"
      sx={{ mb: 2, mt: 2 }}
    >
      <Alert
        severity={config.severity}
        variant="filled"
        onClose={dismissible ? onDismiss : undefined}
        slotProps={{
          closeButton: { 'aria-label': 'Dismiss confirmation' },
        }}
        sx={{ width: '100%', '& .MuiAlert-message': { flex: 1 } }}
      >
        <Box>
          <Typography component="span" variant="body2" sx={{ fontWeight: 500, color: '#fff !important' }}>
            {message}
          </Typography>
          {linkLabel && linkOnClick && (
            <Typography component="span" variant="body2" sx={{ display: 'block', mt: 0.5 }}>
              <Link
                component="button"
                variant="body2"
                onClick={() => {
                  linkOnClick();
                  onDismiss();
                }}
                sx={{ fontWeight: 600, cursor: 'pointer' }}
              >
                {linkLabel}
              </Link>
            </Typography>
          )}
        </Box>
      </Alert>
    </Box>
  );
};

export default ActionConfirmation;
